import type { ItemWithChildren } from '@/components/ap/ai/generateAI/generateAIHelperMethods';
import { create } from 'zustand';

export interface AIGenerateFilters {
  questionType: string | null;
  assessmentCriteria: string[];
  resourceType?: string | null;
  criteriaDetails: any;
  keyConcepts: number[];
  keyConceptsData: ItemWithChildren[];
  relatedConcepts: number[];
  relatedConceptsData: ItemWithChildren[];
  syllabusTopic: string | null;
  syllabusTopicLabel: string | null;
  standardCollectionLabel: string | null;
  commandTermLabel: string | null;
  commandTermId: string | number | null;
  requirements: string | null;
  enhanceCommand: string | null;
  marks: number | null;
  attachments?: any[] | null;
  additionalInstructions: string;
  qbUUID: string | null;
  useCustomPrompt: boolean;
  customPrompt: string;
  globalContextLabel: number | null;
  globalContextData: ItemWithChildren[];
  possibleExplorationsLabel: number | null;
  possibleExplorationsData: ItemWithChildren[];
  atlSkillsLabels: number[];
  atlSkillsData: ItemWithChildren[];
  useExamples: boolean;
  customSyllabusTopics: string;
  paperTypeId: string | number | null;
  paperTypeLabel: string | null;
  teachingLevelId: string | number | null;
  teachingLevelLabel: string | null;
  assessmentObjectiveId: string | number | null;
  assessmentObjectiveLabel: string | null;
  difficultyLevelId: string | number | null;
  difficultyLevelLabel: string | null;
  depthOfKnowledgeId: string | number | null;
  depthOfKnowledgeLabel: string | null;
  categoryId: string | number | null;
  categoryLabel: string | null;
  questionsCount: number | null;
  pdfData?: any;
  extractQuestions?: boolean;
  segmentType?: 'question' | 'resource' | null;
  generateWithImages?: boolean;
  shouldGenerateMarkscheme?: boolean;
  shouldGenerateExplanation?: boolean;
}

export type SidebarIntent = 'generate_segment' | 'enhance_segment' | 'enhance_assessment';
export type EnhanceScope = 'entire' | 'markscheme' | 'explanation';

export type SegmentEnhanceAction =
  | 'proofread'
  | 'generate_variation'
  | 'adjust_difficulty'
  | 'add_markscheme'
  | 'modify_markscheme'
  | 'add_explanation'
  | 'modify_explanation'
  | 'modify_image';

export interface SegmentEnhanceOptions {
  difficultyMode: 'easier' | 'harder' | 'lower_reading' | 'higher_reading' | null;
  checkGrammar: boolean;
  checkSpelling: boolean;
  checkFormatting: boolean;
  checkAnswerKeys: boolean;
}

export interface AIGenerateState {
  mode: 'generate_new' | 'enhance/modify';
  sidebarVisible: boolean;
  sidebarIntent: SidebarIntent;
  enhanceScope: EnhanceScope;
  filters: AIGenerateFilters;
  generatedData: any;
  processedSegments: any[]; // Store processed segment objects
  loading: boolean;
  error: string | null;
  isError: boolean;
  existingContent: any;
  enhanceSourceSegmentId: string | number | null;
  enhanceSourceSegment: any;
  enhanceSourceLabel: string;
  enhanceSourceHasMarkscheme: boolean;
  enhanceSourceHasExplanation: boolean;
  enhanceResult: { type: 'question' | 'markscheme' | 'explanation'; content: string } | null;
  lastAcceptedEnhanceType: 'markscheme' | 'explanation' | null;
  proofreadSummary: string | null;
  proofreadChangesCount: number | null;
  enhanceSegmentAction: SegmentEnhanceAction | null;
  enhanceSegmentOptions: SegmentEnhanceOptions;
  pendingImageUrls: Set<string>;
  enhanceFromForm: boolean;
  enhanceFormCallback: ((field: 'markscheme' | 'explanation', value: string) => void) | null;
  selectedImageForEdit: { url: string; sourceField: string } | null;
  editedImageResult: { url: string; loading: boolean; error: string | null } | null;
  aiGenerateQuestionsConfig?: any;
  /** Experience the panel state belongs to — generated-but-unaccepted results
   *  must never leak into another assessment (cleared on experience switch). */
  panelExperienceId: string | number | null;
}

export interface AIGenerateStoreConfig {
  storageKey: string;
}

interface AIGenerateActions {
  setMode: (mode: 'generate_new' | 'enhance/modify') => void;
  setSidebarVisible: (visible: boolean) => void;
  setSidebarIntent: (intent: SidebarIntent) => void;
  setEnhanceScope: (scope: EnhanceScope) => void;
  setFiltersForGenerate: (filters: Partial<AIGenerateFilters>) => void;
  setGeneratedData: (data: any) => void;
  setProcessedSegments: (segments: any[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setIsError: (isError: boolean) => void;
  setExistingContent: (content: any) => void;
  resetFilters: () => void;
  resetAIGenerateStore: () => void;
  populateFiltersFromSegment: (segment: any) => void;
  refreshEnhanceSourceSegment: (segment: any) => void;
  setEnhanceSegmentAction: (action: SegmentEnhanceAction | null) => void;
  setEnhanceSegmentOptions: (options: Partial<SegmentEnhanceOptions>) => void;
  addPendingImageUrls: (urls: string[]) => void;
  removePendingImageUrl: (url: string) => void;
  setSelectedImageForEdit: (image: { url: string; sourceField: string } | null) => void;
  setEditedImageResult: (
    result: { url: string; loading: boolean; error: string | null } | null
  ) => void;
  setAiGenerateQuestionsConfig: (config: any, options?: { merge?: boolean }) => void;
  triggerReset: () => void;
}

type AIGenerateStore = AIGenerateState & AIGenerateActions & { config: AIGenerateStoreConfig };

export const aiGenerateStoreConfig: AIGenerateStoreConfig = {
  storageKey: 'aiGenerateState',
};

const initialFilters: AIGenerateFilters = {
  questionType: 'long_answer',
  assessmentCriteria: [],
  criteriaDetails: [],
  keyConcepts: [],
  keyConceptsData: [],
  relatedConcepts: [],
  relatedConceptsData: [],
  syllabusTopic: null,
  syllabusTopicLabel: null,
  standardCollectionLabel: null,
  commandTermLabel: null,
  commandTermId: null,
  requirements: null,
  enhanceCommand: null,
  marks: 3,
  attachments: [],
  additionalInstructions: '',
  qbUUID: null,
  useExamples: false,
  useCustomPrompt: false,
  customPrompt: '',
  globalContextLabel: null,
  globalContextData: [],
  possibleExplorationsLabel: null,
  possibleExplorationsData: [],
  atlSkillsLabels: [],
  atlSkillsData: [],
  customSyllabusTopics: '',
  paperTypeId: null,
  paperTypeLabel: null,
  teachingLevelId: null,
  teachingLevelLabel: null,
  assessmentObjectiveId: null,
  assessmentObjectiveLabel: null,
  difficultyLevelId: null,
  difficultyLevelLabel: null,
  depthOfKnowledgeId: null,
  depthOfKnowledgeLabel: null,
  categoryId: null,
  categoryLabel: null,
  questionsCount: null,
  pdfData: undefined,
  extractQuestions: false,
  resourceType: null,
  segmentType: null,
  generateWithImages: false,
  shouldGenerateMarkscheme: false,
  shouldGenerateExplanation: false,
};

const initialState: AIGenerateState = {
  mode: 'generate_new',
  sidebarVisible: false,
  sidebarIntent: 'generate_segment',
  enhanceScope: 'entire',
  filters: initialFilters,
  generatedData: null,
  processedSegments: [],
  loading: false,
  error: null,
  isError: false,
  existingContent: null,
  enhanceSourceSegmentId: null,
  enhanceSourceSegment: null,
  enhanceSourceLabel: '',
  enhanceSourceHasMarkscheme: false,
  enhanceSourceHasExplanation: false,
  enhanceResult: null,
  lastAcceptedEnhanceType: null,
  proofreadSummary: null,
  proofreadChangesCount: null,
  enhanceSegmentAction: null,
  enhanceSegmentOptions: {
    difficultyMode: null,
    checkGrammar: true,
    checkSpelling: true,
    checkFormatting: true,
    checkAnswerKeys: true,
  },
  pendingImageUrls: new Set<string>(),
  enhanceFromForm: false,
  enhanceFormCallback: null,
  selectedImageForEdit: null,
  editedImageResult: null,
  aiGenerateQuestionsConfig: {},
  panelExperienceId: null,
};

export const useAIGenerateStore = create<AIGenerateStore>((set, get) => ({
  ...initialState,
  config: aiGenerateStoreConfig,

  setMode: (mode) => set({ mode }),

  setSidebarVisible: (visible) => set({ sidebarVisible: visible }),

  setSidebarIntent: (intent) => set({ sidebarIntent: intent }),

  setEnhanceScope: (scope) => set({ enhanceScope: scope }),

  setFiltersForGenerate: (newFilters) =>
    set((state) => ({
      filters: {
        ...state.filters,
        ...newFilters,
      },
    })),

  setGeneratedData: (data) => set({ generatedData: data }),

  setProcessedSegments: (segments) => set({ processedSegments: segments }),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error, isError: !!error }),

  setIsError: (isError) => set({ isError }),

  setExistingContent: (content) => set({ existingContent: content }),

  resetFilters: () =>
    set((state) => ({
      filters: initialFilters,
    })),

  resetAIGenerateStore: () => set({ ...initialState }),

  populateFiltersFromSegment: (segment) => {
    const segmentAttributes = segment.attributes ?? segment;
    const questionSegmentAttributes = segmentAttributes.question_segment_attributes ?? {};

    const questionType = questionSegmentAttributes.question_type;

    // Extract content for existing content
    const content =
      questionSegmentAttributes.content ??
      questionSegmentAttributes.title ??
      segmentAttributes.content ??
      segment.content ??
      '';

    // Extract segment ID for replace functionality
    const segmentId = segment.id ?? segmentAttributes.id ?? null;

    // Detect if markscheme and explanation exist (they're top-level columns on question_segments)
    const hasMarkscheme = !!(
      questionSegmentAttributes.markscheme && questionSegmentAttributes.markscheme.trim()
    );
    const hasExplanation = !!(
      questionSegmentAttributes.answer_explanation &&
      questionSegmentAttributes.answer_explanation.trim()
    );

    // Set the mode and populate filters with direct question type
    set((state) => ({
      mode: 'enhance/modify',
      existingContent: content,
      enhanceSourceSegmentId: segmentId,
      enhanceSourceSegment: segment,
      enhanceSourceHasMarkscheme: hasMarkscheme,
      enhanceSourceHasExplanation: hasExplanation,
      filters: {
        ...state.filters,
        questionType: questionType, // Store directly without mapping
        enhanceCommand: null, // Reset enhance command
      },
    }));
  },

  // Post-apply reseed: keeps apply-time snapshots (source segment, content,
  // markscheme/explanation presence) in step with server truth so a second
  // apply in the same panel never builds from generation-time state.
  refreshEnhanceSourceSegment: (segment) => {
    const segmentAttributes = segment?.attributes ?? segment;
    if (!segmentAttributes) return;
    const questionSegmentAttributes = segmentAttributes.question_segment_attributes ?? {};
    // Flat attributes shape — consumers read question_segment_attributes directly.
    set({
      enhanceSourceSegment: segmentAttributes,
      existingContent:
        questionSegmentAttributes.content ??
        questionSegmentAttributes.title ??
        segmentAttributes.content ??
        '',
      enhanceSourceHasMarkscheme: !!(
        questionSegmentAttributes.markscheme && questionSegmentAttributes.markscheme.trim()
      ),
      enhanceSourceHasExplanation: !!(
        questionSegmentAttributes.answer_explanation &&
        questionSegmentAttributes.answer_explanation.trim()
      ),
    });
  },

  setEnhanceSegmentAction: (action) => set({ enhanceSegmentAction: action }),

  setEnhanceSegmentOptions: (options) =>
    set((state) => ({
      enhanceSegmentOptions: { ...state.enhanceSegmentOptions, ...options },
    })),

  addPendingImageUrls: (urls) =>
    set((state) => {
      const next = new Set(state.pendingImageUrls);
      urls.forEach((u) => next.add(u));
      return { pendingImageUrls: next };
    }),

  removePendingImageUrl: (url) =>
    set((state) => {
      const next = new Set(state.pendingImageUrls);
      next.delete(url);
      return { pendingImageUrls: next };
    }),

  setSelectedImageForEdit: (image) => set({ selectedImageForEdit: image, editedImageResult: null }),
  setEditedImageResult: (result) => set({ editedImageResult: result }),

  setAiGenerateQuestionsConfig: (config, options = { merge: true }) =>
    set((state) => {
      if (options.merge) {
        // Default behavior: merge the provided config properties into the existing config
        return {
          aiGenerateQuestionsConfig: {
            ...state.aiGenerateQuestionsConfig,
            ...config,
          },
        };
      } else {
        // Replace completely when merge is explicitly false
        return { aiGenerateQuestionsConfig: config };
      }
    }),

  triggerReset: () => {
    // Use a global ref to store the callback to avoid infinite loops
    if (globalResetCallback.current) {
      globalResetCallback.current();
    }
  },
}));

// Global ref to store the reset callback outside of the store state
const globalResetCallback: { current: (() => void) | null } = { current: null };

// Function to set the global reset callback
export const setGlobalResetCallback = (callback: (() => void) | null) => {
  globalResetCallback.current = callback;
};

// Selector functions for cleaner component access
export const selectAIFilters = (state: AIGenerateStore) => state.filters;
export const selectAIGeneratedData = (state: AIGenerateStore) => state.generatedData;
export const selectAIProcessedSegments = (state: AIGenerateStore) => state.processedSegments;
export const selectAILoading = (state: AIGenerateStore) => state.loading;
export const selectAIMode = (state: AIGenerateStore) => state.mode;
export const selectAIError = (state: AIGenerateStore) => state.error;
export const selectAIIsError = (state: AIGenerateStore) => state.isError;
export const selectAIExistingContent = (state: AIGenerateStore) => state.existingContent;
export const selectAIGenerateQuestionsConfig = (state: AIGenerateStore) =>
  state.aiGenerateQuestionsConfig;
export const selectTriggerReset = (state: AIGenerateStore) => state.triggerReset;
export const selectPendingImageUrls = (state: AIGenerateStore) => state.pendingImageUrls;
export const selectSidebarVisible = (state: AIGenerateStore) => state.sidebarVisible;
export const selectSidebarIntent = (state: AIGenerateStore) => state.sidebarIntent;
export const selectEnhanceScope = (state: AIGenerateStore) => state.enhanceScope;
export const selectEnhanceSegmentAction = (state: AIGenerateStore) => state.enhanceSegmentAction;
